import React from 'react';
import { ThemeProvider } from 'next-themes';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Outlet, useOutletContext } from 'react-router-dom';
import './i18n';
import './App.css';
import { UserProvider, useUser } from '@/contexts/UserContext';
import { StatsProvider } from '@/contexts/StatsContext';
import ScrollToTop from './components/ScrollToTop';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import { supabase } from '@/lib/supabase';
import axios from 'axios';
import Splash from './pages/Splash';
import Login from './pages/Login';
import GetStarted from './pages/GetStarted';
import Welcome from './pages/Welcome';
import Onboarding from './pages/OnboardingV1';
import OnboardingV2 from './pages/OnboardingV2';
import Dashboard from './pages/Dashboard';
import DashboardV2 from './pages/DashboardV2';
import Profile from './pages/Profile';
import EditProfile from './pages/EditProfile';
import History from './pages/History';
import Events from './pages/Events';
import Stats from './pages/Stats';
import Notes from './pages/Notes';
import Settings from './pages/Settings';
import Metrics from './pages/Metrics';
import Subscription from './pages/Subscription';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import PremiumSuccess from './pages/PremiumSuccess';
import PremiumCancel from './pages/PremiumCancel';
import DebugLogs from './pages/DebugLogs';
import CapacitorInit from './components/CapacitorInit';
import IAPTransactionObserver from './components/IAPTransactionObserver';
import { Toaster } from 'sonner';
import AuthCallback from './pages/AuthCallback';
import DownloadApp from './pages/DownloadApp';
import LandingSupport from './pages/LandingSupport';
// The marketing homepage is a static file (frontend/public/landing/home/index.html)
// served at "/" by the Vercel rewrite — see vercel.json. In dev, /landing/home
// hard-redirects to "/" below.
const StaticLandingRedirect = () => {
  React.useEffect(() => { window.location.replace('/'); }, []);
  return null;
};
// Root route handler: on mobile, direct unauthenticated users to /get-started;
// on web, serve /onboarding directly at the root URL.
const RootRedirect = ({ userId, onComplete }) => {
  if (userId) {
    return <Navigate to="/dashboard" replace />;
  }
  const isMobile = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.();
  if (isMobile) {
    return <Navigate to="/get-started" replace />;
  }
  return <OnboardingV2 onComplete={onComplete} />;
};
import LandingPrivacy from './pages/LandingPrivacy';
import LandingTerms from './pages/LandingTerms';
import Support from './pages/Support';
import PreviewSlideMenu from './pages/PreviewSlideMenu';
import PreviewBottomNav from './pages/PreviewBottomNav';
import PreviewStats from './pages/PreviewStats';
import PreviewSupport from './pages/PreviewSupport';
import PreviewPaywalls from './pages/PreviewPaywalls';
import NotFound from './pages/NotFound';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import ActionDetail from './pages/ActionDetail';
import Guide from './pages/Guide';
import NotificationsPage from './pages/NotificationsPage';
import Achievements from './pages/Achievements';
import Plan from './pages/Plan';
import PreviewV2Theme from './pages/PreviewV2Theme';
import i18n, { detectGeoLanguage } from './i18n';
import { trackAppOpen, trackFirstOpen, getOrCreateAnonId, flush } from '@/lib/analytics';

// ...existing code...

// I18n Provider component — tracks language for context but does NOT use key=
// to avoid destructive tree remounts. react-i18next's useTranslation hook
// already triggers re-renders in every component that calls t().
const I18nProvider = ({ children }) => {
  const [, setCurrentLang] = React.useState(i18n.language);

  React.useEffect(() => {
    const handleLanguageChange = (lng) => {
      setCurrentLang(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    // Auto-detect language from IP for first-time visitors
    detectGeoLanguage();
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  return <>{children}</>;
};

import Admin from './pages/Admin';

// The game is only needed on /play, so it loads in its own chunk.
const HusbandSimulator = React.lazy(() => import('./game/HusbandSimulator'));

/**
 * SubscriptionGate – wraps authenticated routes.
 * If the user's trial has expired (and they have no active subscription),
 * every page redirects to /subscription so the paywall is the only thing they see.
 */
const SubscriptionGate = () => {
  const { loading } = useUser();
  const outletContext = useOutletContext();
  if (loading) return null;
  return <Outlet context={outletContext} />;
};


function AppContent() {
  // ScrollToTop import già in alto, rimosso duplicato
  const navigate = useNavigate();
  const [userId, setUserId] = React.useState(null);
  const [reminderTime, setReminderTime] = React.useState('19:00');
  const [checkinTime, setCheckinTime] = React.useState('22:00');
  const [authLoading, setAuthLoading] = React.useState(true);

  // ...existing code...

  // Navigazione dashboard
  React.useEffect(() => {
    // ── Fire first_open + anonymous app_open immediately on launch ──
    // This happens BEFORE auth resolves so every install is counted,
    // whether or not the user ever creates an account.
    const anonId = getOrCreateAnonId();
    const platform = typeof window !== 'undefined' && window.Capacitor?.getPlatform
      ? window.Capacitor.getPlatform()
      : 'web';
    trackFirstOpen(anonId, { platform });
    trackAppOpen(anonId, { isAnonymous: true });
    flush();

    // ── CRITICAL FAILSAFE: Force splash screen to hide after 5s on native ──
    // This is a last-resort backstop that guarantees the splash never hangs forever,
    // even if CapacitorInit fails, auth hangs, or any other boot-time issue occurs.
    // The splash should already be hidden by CapacitorInit within ~500ms, but this
    // ensures we never violate App Store guideline 2.1(a) due to infinite splash.
    if (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()) {
      const failsafeTimer = setTimeout(() => {
        console.warn('[App] Failsafe: forcing splash hide after 5s');
        if (window.Capacitor?.Plugins?.SplashScreen) {
          window.Capacitor.Plugins.SplashScreen.hide().catch(() => {});
        }
      }, 5000);
      // Clean up timer when component unmounts (though App never unmounts in practice)
      return () => clearTimeout(failsafeTimer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const initializeAuth = async () => {
      try {
        // TEST-HOOK: allow E2E to bypass Supabase auth by setting
        // localStorage.E2E_BYPASS_AUTH = '1' and appropriate localStorage keys.
        // This keeps the behavior fully opt-in for test runs only.
        const e2eBypass = typeof window !== 'undefined' && localStorage.getItem('E2E_BYPASS_AUTH') === '1';
        const e2eUserId = e2eBypass ? localStorage.getItem('betterhusbandUserId') : null;
        console.log('[App] E2E_BYPASS_AUTH:', e2eBypass, 'betterhusbandUserId:', e2eUserId);
        if (e2eBypass && e2eUserId) {
          setUserId(e2eUserId);
          setAuthLoading(false);
          return;
        }

        // Additional test helper: if an auth token is present in localStorage (set by tests),
        // extract the user id from it so supabase.getSession isn't strictly required in tests.
        if (e2eBypass) {
          try {
            const raw = localStorage.getItem('sb-ixfcdbyhpyxdnozixfiz-auth-token');
            if (raw) {
              const parsed = JSON.parse(raw);
              const tokenUserId = parsed?.currentSession?.user?.id || parsed?.currentUser?.id;
              if (tokenUserId) {
                setUserId(tokenUserId);
                setAuthLoading(false);
                return;
              }
            }
          } catch (err) {
            console.warn('[App] Failed to parse Supabase token from localStorage', err);
          }
        }

        // Check if we have a valid Supabase session with timeout (5s max)
        // Prevents blocking app launch if Supabase SDK hangs on iPadOS or slow networks
        const { data: { session }, error } = await Promise.race([
          supabase.auth.getSession(),
          new Promise((resolve) => setTimeout(() => resolve({ data: { session: null }, error: new Error('getSession timeout') }), 5000))
        ]);
        
        if (session?.user?.id && !error) {
          // We have a valid session, set the userId
          setUserId(session.user.id);
          // KPI: track authenticated app open, carrying the anon ID so we can
          // link the device (first_open) to this account in the admin panel.
          const storedAnonId = localStorage.getItem('bh_anon_tracking_id');
          trackAppOpen(session.user.id, { anonymous_id: storedAnonId || undefined });
          flush();
        } else {
          // No valid session, clear any stored userId
          localStorage.removeItem('betterhusbandUserId');
          setUserId(null);
        }
      } catch (error) {
        console.error('Error checking auth session:', error);
        localStorage.removeItem('betterhusbandUserId');
        setUserId(null);
      } finally {
        setAuthLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user?.id) {
          setUserId(session.user.id);
        } else {
          setUserId(null);
          localStorage.removeItem('betterhusbandUserId');
        }
        setAuthLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Suppress Supabase invalid refresh token errors
  React.useEffect(() => {
    const handleUnhandledRejection = (event) => {
      if (event.reason?.message?.includes('Invalid Refresh Token') || 
          event.reason?.message?.includes('Refresh Token Not Found')) {
        event.preventDefault();
        // ...existing code...
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const handleLogin = (newUserId) => {
    // ...existing code...
    if (window.Capacitor && window.Capacitor.isNativePlatform) {
      //alert(`[DEBUG] handleLogin called - newUserId: ${newUserId}`);
    }
    setUserId(newUserId);
    localStorage.setItem('betterhusbandUserId', newUserId);
  };

  const handleOnboardingComplete = (newUserId) => {
    if (newUserId) {
      setUserId(newUserId);
      localStorage.setItem('betterhusbandUserId', newUserId);
    }
  };

  const handleLogout = async () => {
    // Clear Supabase session — signOut can deadlock on Capacitor Preferences,
    // so give it 3s max before proceeding with local cleanup.
    try {
      await Promise.race([
        supabase.auth.signOut(),
        new Promise(r => setTimeout(r, 3000)),
      ]);
    } catch (_) {}
    // Clear state and storage
    setUserId(null);
    localStorage.removeItem('betterhusbandUserId');
    localStorage.removeItem('betterhusbandUser');
    localStorage.removeItem('betterhusbandUserName');
    localStorage.removeItem('betterhusbandGooglePicture');
    localStorage.removeItem('betterhusbandGoglePicture');
    localStorage.setItem('betterhusbandExplicitLogout', '1');
    // Clear axios auth defaults
    delete axios.defaults.headers.common.Authorization;
    delete axios.defaults.headers.common['X-User-Id'];
    delete axios.defaults.headers.common['x-user-id'];
    navigate('/');
  };

  // Show loading while checking auth.
  // CRITICAL: render CapacitorInit here too (it returns null) so the native
  // splash hides + status bar configures at first paint instead of waiting
  // until auth resolves. Otherwise iOS visibly flickers between black splash,
  // white loading screen, and final status-bar reconfig.
  //
  // To avoid a flash of the spinner when auth resolves quickly (the common
  // case with a cached Supabase session), the spinner is delayed by 300ms
  // via CSS — so for fast resolutions the user sees an unbroken #FAFAF9
  // background continuous with the native splash.
  if (authLoading) {
    console.log('[FLICKER]', performance.now().toFixed(0), 'ms — render: AUTH_LOADING screen');
    return (
      <>
        <CapacitorInit userId={null} reminderTime={reminderTime} checkinTime={checkinTime} />
        <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
          <div
            className="w-6 h-6 border-2 border-stone-900 border-t-transparent rounded-full animate-spin opacity-0"
            style={{ animation: 'spin 1s linear infinite, bhFadeIn 0.2s ease-out 0.3s forwards' }}
          />
        </div>
      </>
    );
  }
  console.log('[FLICKER]', performance.now().toFixed(0), 'ms — render: ROUTES (userId=' + (userId ? 'yes' : 'no') + ')');

  // ...existing code...

  try {
    return (
      <I18nProvider>
        <UserProvider>
          <StatsProvider>
            <CapacitorInit userId={userId} reminderTime={reminderTime} checkinTime={checkinTime} />
            <IAPTransactionObserver userId={userId} />
            <ScrollToTop />
            <Routes>
        <Route path="/" element={<RootRedirect userId={userId} onComplete={handleOnboardingComplete} />} />
        <Route path="/get-started" element={userId ? <Navigate to="/dashboard" replace /> : <GetStarted />} />
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route path="/landing" element={<Navigate to="/landing/home" replace />} />
        <Route path="/landing/it" element={<Navigate to="/landing/home" replace />} />
        <Route path="/landing/it2" element={<Navigate to="/landing/home" replace />} />
        <Route path="/landing/en" element={<Navigate to="/landing/home" replace />} />
        <Route path="/landing/en2" element={<Navigate to="/landing/home" replace />} />
        <Route path="/landing/en_list" element={<Navigate to="/landing/home" replace />} />
        <Route path="/landing/it_list" element={<Navigate to="/landing/home" replace />} />
        <Route path="/landing/en_quiz" element={<Navigate to="/landing/home" replace />} />
        <Route path="/landing/689test" element={<Navigate to="/landing/home" replace />} />
        <Route path="/landing/v3" element={<Navigate to="/landing/home" replace />} />
        <Route path="/landing/support" element={<LandingSupport />} />
        <Route path="/landing/home" element={<StaticLandingRedirect />} />
        <Route path="/landing/privacy" element={<LandingPrivacy />} />
        <Route path="/landing/terms" element={<LandingTerms />} />
        <Route path="/admin" element={<Admin />} />
        {/* /admin/<section>; old names like /admin/tickets redirect inside Admin. */}
        <Route path="/admin/:section" element={<Admin />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/onboarding" element={<OnboardingV2 onComplete={handleOnboardingComplete} />} />
        {/* Campaign-targetable localized entry (e.g. /onboarding/en, /onboarding/it)
            so Reddit/paid traffic drops straight into the right language flow. */}
        <Route path="/onboarding/:lang" element={<OnboardingV2 onComplete={handleOnboardingComplete} />} />
        <Route path="/onboarding-v2" element={<OnboardingV2 onComplete={handleOnboardingComplete} />} />
        <Route path="/auth/callback" element={<AuthCallback onLogin={handleLogin} />} />

        {/* Single shared layout – Header, Garden & BottomNav stay mounted across page switches */}
        <Route element={userId ? <AuthenticatedLayout /> : <Navigate to="/" replace />}>
          {/* Subscription page is always accessible (paywall for expired trials) */}
          <Route path="/subscription" element={<Subscription />} />

          {/* All other pages are gated: expired trial → redirected to /subscription */}
          <Route element={<SubscriptionGate />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/action-detail" element={<ActionDetail />} />
            <Route path="/stats" element={<Stats />} />
            <Route path="/plan" element={<Plan />} />
            <Route path="/achievements" element={<Achievements />} />
            <Route path="/notes" element={<Notes />} />
            <Route path="/events" element={<Events />} />
            <Route path="/history" element={<History />} />
            <Route path="/profile" element={<Profile userId={userId} />} />
            <Route path="/profile/edit" element={<EditProfile userId={userId} />} />
            <Route path="/metrics" element={<Metrics />} />
            <Route path="/settings" element={<Settings userId={userId} />} />
            <Route path="/support" element={<Support />} />
            <Route path="/guide" element={<Guide />} />
            <Route path="/notifications" element={<NotificationsPage />} />
          </Route>
        </Route>

        {/* Dev-only previews (no auth) */}
        <Route path="/__preview/slide-menu" element={<PreviewSlideMenu />} />
        <Route path="/__preview/bottom-nav" element={<PreviewBottomNav />} />
        <Route path="/__preview/stats" element={<PreviewStats />} />
        <Route path="/__preview/support" element={<PreviewSupport />} />
        <Route path="/__preview/paywalls" element={<PreviewPaywalls />} />
        <Route path="/dashboard-v2" element={<DashboardV2 />} />
        <Route path="/__preview/v2-theme" element={<PreviewV2Theme />} />

        {/* Each language gets its own URL so both can be indexed; see
            lib/blogPaths.js. English keeps the original paths. */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/it/blog" element={<Blog />} />
        <Route path="/it/blog/:slug" element={<BlogPost />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/premium/success" element={<PremiumSuccess />} />
        <Route path="/premium/cancel" element={<PremiumCancel />} />
        <Route path="/download-app" element={<DownloadApp />} />
        <Route path="/play" element={<React.Suspense fallback={null}><HusbandSimulator /></React.Suspense>} />

        {/* Hidden in-app log viewer (no auth) — open /debug/logs to copy
            the on-device log file to clipboard for sharing. */}
        <Route path="/debug/logs" element={<DebugLogs />} />

        {/* 404 - deve essere l'ultima route */}
        <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </StatsProvider>
      </UserProvider>
    </I18nProvider>
    );
  } catch (error) {
    console.error(error);
    // ...existing code...
    return (
      <div className="min-h-screen bg-red-100 flex items-center justify-center p-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-800 mb-4">Errore App</h1>
          <p className="text-red-600">{error.message}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded"
          >
            Ricarica
          </button>
        </div>
      </div>
    );
  }
}

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ThemeProvider>
  );
}
